import { Stack, Button, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Dayjs } from "dayjs";
import { PasteFromClipboardButton } from "../consumption/PasteFromClipboardButton";
import { useTenantTranslation } from "hooks/formatters";
import type { PasteUpdate } from "./types";
import { SafeDatePicker } from "components/SafeDatePicker";

interface DeliveryAdjustmentsToolbarProps {
  selectedMonth: Dayjs;
  setSelectedMonth: (month: Dayjs) => void;
  hasRows: boolean;
  hasChanges: boolean;
  onSave: () => void | Promise<void>;
  onReset: () => void;
  onApplyPastedUpdates: (updates: PasteUpdate[]) => void;
  children?: React.ReactNode;
}

export const DeliveryAdjustmentsToolbar = ({
  selectedMonth,
  setSelectedMonth,
  hasRows,
  hasChanges,
  onSave,
  onReset,
  onApplyPastedUpdates,
  children,
}: DeliveryAdjustmentsToolbarProps) => {
  const { t } = useTenantTranslation();

  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      useFlexGap
      sx={{ flexWrap: "wrap" }}
    >
      <Stack direction="row" spacing={0}>
        <IconButton
          aria-label={t("previous")}
          size="small"
          onClick={() => {
            setSelectedMonth(selectedMonth.subtract(1, "month"));
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <SafeDatePicker
          label={t("month")}
          views={["month", "year"]}
          value={selectedMonth}
          onChange={(newValue) => {
            if (newValue) {
              setSelectedMonth(newValue);
            }
          }}
          slotProps={{
            textField: { size: "small" },
          }}
        />
        <IconButton
          aria-label={t("next")}
          size="small"
          onClick={() => {
            setSelectedMonth(selectedMonth.add(1, "month"));
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>
      <Button
        variant="contained"
        onClick={() => void onSave()}
        disabled={!hasRows || !hasChanges}
      >
        {t("create")}
      </Button>
      <Button variant="outlined" onClick={onReset} disabled={!hasChanges}>
        {t("reset")}
      </Button>
      <PasteFromClipboardButton
        size="medium"
        variant="outlined"
        onApply={onApplyPastedUpdates}
      />
      {children}
    </Stack>
  );
};
